import React, { useEffect, useState, useRef } from 'react'

import { useStoreActions, useStoreState } from 'store'
import CurrencyInput from 'components/shared/CurrencyInput'

const tooltip = (above: boolean) => (
  <div className="Tooltip display-block" data-testid="loanAmount-tooltip">
    <div
      className={`Tooltip-content Tooltip-content--anchorCenter z-layer-one ${
        above ? '' : 'Tooltip-content--belowTrigger'
      }`}
      role="tooltip"
    >
      <div className="Tooltip-body">
        <div className="Tooltip-arrow"></div>
        <p className="text-slate type-body-three p-2">
          To see your personalized cash-out rates, just add the extra amount you’d like to borrow to your current loan
          balance. This will be your new loan amount.
        </p>
      </div>
    </div>
  </div>
)

const LoanAmount = (props: JSX.IntrinsicAttributes & { className?: string }) => {
  const { onInputTracking } = useStoreActions(actions => actions.tracking)
  const { updateDimensions } = useStoreActions(actions => actions.dimensions)
  const { loanAmount, cashOutAmount } = useStoreState(state => state.dimensions.dimensions.refinance)
  const { dimensionsValidation, dimensionsRecentlyChanged } = useStoreState(state => state.dimensions)
  const offers = useStoreState(state => state.offers.offers)
  const isLoading = useStoreState(state => state.offers.isLoading)!
  const showPostValidation =
    offers.filter(o => !['editorial', 'editorial-disclosure'].includes(o.type)).length === 0 &&
    !isLoading &&
    !dimensionsRecentlyChanged
  let validationErrorMsg

  switch (true) {
    case dimensionsValidation.refinance.loanAmount.invalid:
      validationErrorMsg = dimensionsValidation.refinance.loanAmount.errorMsg
      break
    case dimensionsValidation.refinance.cashOutAmount.invalid:
      validationErrorMsg = dimensionsValidation.refinance.cashOutAmount.errorMsg
      break
    case dimensionsValidation.refinance.loanAmountHigh.invalid:
      validationErrorMsg = dimensionsValidation.refinance.loanAmountHigh.errorMsg
      break
    default:
      validationErrorMsg = dimensionsValidation.refinance.maxLtvRatio.errorMsg
  }

  const isDesktop = useStoreState(state => state.dimensions.isDesktop)
  const hideLoanAmountTooltip = useStoreState(state => state.initialization.hideLoanAmountTooltip)
  const { setHideLoanAmountTooltip } = useStoreActions(actions => actions.initialization)
  const inputRef = useRef<HTMLDivElement | null>(null)
  const [inputInView, setinputInView] = useState(true)

  // need to ensure that the tooltip does not cover up loan balance/cash out errors
  // on desktop
  const { invalid: loanAmountInvalid } = dimensionsValidation.refinance.loanAmount
  const { invalid: cashOutAmountInvalid } = dimensionsValidation.refinance.cashOutAmount
  const { invalid: cashOutLtvRatioInvalid } = dimensionsValidation.refinance.maxLtvRatio

  const unCoverCashOutError = (loanAmountInvalid || cashOutAmountInvalid || cashOutLtvRatioInvalid) && isDesktop

  const handleChange = (value: number) => {
    updateDimensions({ refinance: { loanAmount: value } })
    onInputTracking({ name: 'loanamount', type: 'BALANCE', label: 'loan amount', value })
  }

  useEffect(() => {
    if (cashOutAmount) {
      const clickToHideTooltip = (event: MouseEvent) => {
        if ((event.target as HTMLElement).id !== 'loanAmount') {
          setHideLoanAmountTooltip(true)
        }
      }
      // clicking anywhere else on the page should hide tooltip
      window.document.body.addEventListener('click', clickToHideTooltip)
      return () => window.document.body.removeEventListener('click', clickToHideTooltip)
    }
  }, [cashOutAmount, setHideLoanAmountTooltip])

  useEffect(() => {
    // The tooltip should hide once a user scrolls past the RT on Desktop
    if (isDesktop && 'IntersectionObserver' in window) {
      let observerRefValue: Element | null = null
      const observer = new IntersectionObserver(
        ([entry]) => {
          setinputInView(entry.isIntersecting)
        },
        { threshold: 0.5 }
      )
      if (inputRef.current) {
        observer.observe(inputRef.current)
        observerRefValue = inputRef.current
      }
      return () => {
        if (observerRefValue) {
          observer.unobserve(observerRefValue)
        }
      }
    }
  }, [isDesktop])

  useEffect(() => {
    // need to ensure that the tooltip does not cover up any loan amount errors
    // on desktop
    if (unCoverCashOutError) setHideLoanAmountTooltip(true)
  }, [unCoverCashOutError, setHideLoanAmountTooltip])

  return (
    <>
      {!isDesktop && !hideLoanAmountTooltip && cashOutAmount ? tooltip(true) : null}
      <label className="FormLabel right-addon" htmlFor="loanAmount">
        {cashOutAmount ? 'Loan balance + Cash-out' : 'Loan balance'}
      </label>
      <div
        className={`FormControl with-left-icon ${props.className}`}
        data-format="$"
        style={{ maxWidth: 'initial' }}
        ref={inputRef}
      >
        <CurrencyInput
          id="loanAmount"
          testid="loanAmount"
          value={loanAmount}
          update={handleChange}
          invalid={
            dimensionsValidation.refinance.loanAmount.invalid ||
            dimensionsValidation.refinance.cashOutAmount.invalid ||
            dimensionsValidation.refinance.loanAmountHigh.invalid ||
            (showPostValidation && dimensionsValidation.refinance.maxLtvRatio.invalid)
          }
          errorMsg={validationErrorMsg}
        />
      </div>
      {isDesktop && !hideLoanAmountTooltip && cashOutAmount && inputInView ? tooltip(false) : null}
    </>
  )
}

export default LoanAmount
